import React, { useState, useEffect, useRef } from 'react';
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSync, faRocket, faCheck, faExclamationTriangle, faCog,
  faPlay, faUser, faServer, faTicketAlt, faClock,
} from '@fortawesome/free-solid-svg-icons';
import { agentsApi } from '../../services/agentsApi';
import type { ActivityEvent } from '../../types/agents';

const EVENT_ICONS: Record<string, any> = {
  VALIDATION_CREATED: faRocket,
  AGENT_STARTED: faPlay,
  AGENT_PROGRESS: faCog,
  AGENT_COMPLETED: faCheck,
  AGENT_ERROR: faExclamationTriangle,
  APP_RUNNING: faServer,
  NEEDS_INPUT: faUser,
  TICKET_CREATED: faTicketAlt,
  TICKET_IN_PROGRESS: faPlay,
  TICKET_COMPLETED: faCheck,
  TICKET_NEEDS_HUMAN: faUser,
  TICKET_CANCELLED: faExclamationTriangle,
  STATUS_CHANGED: faCog,
};

const EVENT_COLORS: Record<string, string> = {
  AGENT_COMPLETED: 'text-green-500',
  TICKET_COMPLETED: 'text-green-500',
  APP_RUNNING: 'text-green-500',
  AGENT_ERROR: 'text-red-500',
  TICKET_NEEDS_HUMAN: 'text-orange-500',
  NEEDS_INPUT: 'text-orange-500',
  AGENT_STARTED: 'text-blue-500',
  TICKET_IN_PROGRESS: 'text-blue-500',
};

function timeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

interface Props {
  validationId: string;
}

const ActivityTab: React.FC<Props> = ({ validationId }) => {
  const [events, setEvents] = useState<ActivityEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const lastTimestampRef = useRef<string | undefined>();

  const fetchEvents = async (isRefresh = false) => {
    try {
      if (!isRefresh) setLoading(true);
      const params: any = { limit: 50 };
      // Use 'since' for incremental polling
      if (isRefresh && lastTimestampRef.current) {
        params.since = lastTimestampRef.current;
      }
      const { data } = await agentsApi.getActivityTimeline(validationId, params);

      let parsed: ActivityEvent[] = [];
      if (data?.content?.[0]?.text) {
        try {
          const inner = JSON.parse(data.content[0].text);
          parsed = inner.events || inner || [];
        } catch {
          parsed = [];
        }
      } else if (Array.isArray(data)) {
        parsed = data;
      }

      if (isRefresh && parsed.length > 0) {
        // Prepend new events
        setEvents((prev) => {
          const existingIds = new Set(prev.map((e) => e.id));
          const newEvents = parsed.filter((e) => !existingIds.has(e.id));
          return [...newEvents, ...prev];
        });
      } else if (!isRefresh) {
        setEvents(parsed);
      }

      if (parsed.length > 0) {
        lastTimestampRef.current = parsed[0]?.createdAt;
      }
    } catch (err: any) {
      if (!isRefresh) {
        toast.error(err?.response?.data?.detail || 'Failed to load activity');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEvents();
    const interval = setInterval(() => fetchEvents(true), 10000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [validationId]);

  if (loading && events.length === 0) {
    return (
      <div className="flex items-center justify-center py-16">
        <FontAwesomeIcon icon={faSync} className="animate-spin text-indigo-500 text-xl mr-3" />
        <span className="text-gray-500">Loading activity...</span>
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-end mb-3">
        <button onClick={() => fetchEvents()} className="text-sm text-indigo-600 hover:text-indigo-800 flex items-center gap-1">
          <FontAwesomeIcon icon={faSync} />
          Refresh
        </button>
      </div>

      {events.length === 0 ? (
        <div className="text-gray-400 py-8 text-center">No activity yet</div>
      ) : (
        <div className="relative">
          {/* Timeline line */}
          <div className="absolute left-4 top-2 bottom-2 w-px bg-gray-200" />

          <div className="space-y-1">
            {events.map((event) => {
              const icon = EVENT_ICONS[event.type] || faClock;
              const color = EVENT_COLORS[event.type] || 'text-gray-400';

              return (
                <div key={event.id} className="flex items-start gap-3 pl-1 py-2">
                  <div className={`w-7 h-7 rounded-full bg-white border-2 border-gray-200 flex items-center justify-center flex-shrink-0 z-10 ${color}`}>
                    <FontAwesomeIcon icon={icon} className="text-xs" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-gray-900">{event.title}</span>
                      <span className="text-xs text-gray-400">{event.createdAt ? timeAgo(event.createdAt) : ''}</span>
                    </div>
                    {event.description && (
                      <p className="text-xs text-gray-500 mt-0.5 truncate">{event.description}</p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default ActivityTab;
